// ─────────────────────────────────────────────────────────────────────────────
// Comportamento de TECLADO partilhado pelas duas apps.
//
// Isto vive em `packages/shared` — que é deliberadamente SEM React — porque é
// DOM puro: recebe um elemento e devolve a limpeza. Cada app embrulha-o num
// hook de três linhas. Sem isto, o mesmo comportamento seria escrito duas vezes
// e divergiria como já divergiram os chips, os botões e a conversa do Hexis.
// ─────────────────────────────────────────────────────────────────────────────

/** O que é focável por teclado, por ordem de documento. */
const FOCAVEL = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

export function focaveisDentro(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>(FOCAVEL))
    .filter((e) => e.offsetWidth > 0 || e.offsetHeight > 0 || e === document.activeElement);
}

/**
 * Dá a um diálogo o comportamento que um diálogo tem de ter:
 *
 *   • Escape fecha — sem isto, quem navega por teclado fica preso a ter de
 *     encontrar o ✕ à letra;
 *   • o foco ENTRA no diálogo ao abrir (senão o Tab seguinte continua atrás
 *     dele, na página que já não se vê);
 *   • o Tab circula DENTRO (um diálogo que deixa o foco fugir para trás é um
 *     diálogo que, para um leitor de ecrã, nunca se abriu);
 *   • ao fechar, o foco VOLTA a quem o abriu.
 *
 * Devolve a função de limpeza — o chamador liga-a ao desmonte.
 */
export function ligarDialogo(el: HTMLElement, aoFechar: () => void): () => void {
  const anterior = document.activeElement as HTMLElement | null;

  // O primeiro focável, ou o próprio diálogo (que precisa de tabindex para o poder ser).
  const alvos = focaveisDentro(el);
  if (alvos[0]) alvos[0].focus();
  else { el.tabIndex = -1; el.focus(); }

  const aoTecla = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); aoFechar(); return; }
    if (e.key !== 'Tab') return;
    const f = focaveisDentro(el);
    if (!f.length) return;
    const primeiro = f[0]!, ultimo = f[f.length - 1]!;
    const atual = document.activeElement;
    // Só se intervém nas PONTAS: no meio, o Tab do browser já faz o certo.
    if (e.shiftKey && (atual === primeiro || !el.contains(atual))) {
      e.preventDefault(); ultimo.focus();
    } else if (!e.shiftKey && atual === ultimo) {
      e.preventDefault(); primeiro.focus();
    }
  };

  document.addEventListener('keydown', aoTecla, true);
  return () => {
    document.removeEventListener('keydown', aoTecla, true);
    // Devolver o foco a quem abriu — mas só se ele ainda pertencer ao diálogo.
    // O teste `el.contains(activeElement)` sozinho FALHA no caso normal: quando
    // o React desmonta o painel, o elemento focado desaparece com ele e o foco
    // cai no <body> ANTES desta limpeza correr. Aí `contains` dá falso e o foco
    // ficava perdido no topo da página — exatamente o que um utilizador de
    // teclado não pode ter depois de fechar um diálogo com Escape.
    const activo = document.activeElement as HTMLElement | null;
    const perdido = !activo || activo === document.body || !document.contains(activo);
    if (anterior && document.contains(anterior) && (perdido || el.contains(activo))) {
      anterior.focus();
    }
  };
}
